import { use, useCallback } from "react";
import { objectEntries } from "ts-extras";

import { LocaleContext } from "~/components/LocaleContext";
import { messages } from "~/i18n/messages";

type ExtractPlaceholders<
  T extends string,
  Acc = never,
> = T extends `${string}{${infer Placeholder}}${infer Rest}`
  ? ExtractPlaceholders<Rest, Acc | Placeholder>
  : Acc;

type MessageReplacements = {
  [K in keyof typeof messages]: ExtractPlaceholders<
    (typeof messages)[K]["en-US"]
  > extends never
    ? undefined
    : Record<ExtractPlaceholders<(typeof messages)[K]["en-US"]>, string>;
};

type TFunction = <
  K extends keyof typeof messages,
  R extends MessageReplacements[K],
>(
  key: K,
  ...args: R extends undefined ? [] : [replacements: R]
) => string;

export const useLocale = (locale = use(LocaleContext)) => {
  const t = useCallback<TFunction>(
    (key, ...args) => {
      let message = messages[key][locale].toString();

      const replacements = args[0];
      if (replacements) {
        objectEntries(replacements).forEach(([key, value]) => {
          message = message.replace(`{${key}}`, value);
        });
      }

      return message;
    },
    [locale],
  );
  return {
    t,
    locale,
  };
};
